import React from 'react';
import { sortBy } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { addDays, isSameDay } from 'date-fns';
import { mapRoute } from '#/transit-tracker/utils/routes';
import { parseSignVanityName } from '#/shared/utils/transit';
import { FEEDS, TRAIN_ALL_CODES } from '#/shared/constants';
import { getNewTriMetDate } from '#/shared/utils/date-time';
import { formatNamespacedStopId } from '#/shared/utils/agency-utils';

const checkIsTrain = arrival => {
  const numId = Number(arrival?.route?.id);
  return (
    arrival?.route?.feedId === FEEDS.TRIMET &&
    TRAIN_ALL_CODES.some(code => code === numId)
  );
};

export const getArrivingVehicleType = arrival => {
  return checkIsTrain(arrival) ? (
    <FormattedMessage id="arrivals.train" />
  ) : (
    <FormattedMessage id="arrivals.bus" />
  );
};

export const getArrivalScheduledLabel = scheduledTime => {
  const today = getNewTriMetDate();
  const tomorrow = addDays(today, 1);
  const scheduledDate = getNewTriMetDate(scheduledTime);

  if (isSameDay(scheduledDate, today)) {
    return <FormattedMessage id="arrivals.scheduled" />;
  }

  if (isSameDay(scheduledDate, tomorrow)) {
    return <FormattedMessage id="arrivals.tomorrow" />;
  }

  // if scheduled time is further than a day out, use the day of the week;
  // this expression translates the day value to the browser's selected language
  return scheduledDate.toLocaleDateString(window.navigator.language, {
    weekday: 'long',
  });
};

/** @returns {import('#/transit-tracker/types').HighlightedStop} */
export const getStopFromArrival = arrival => {
  const feedId = arrival.feedId || FEEDS.TRIMET;
  return {
    id: `${feedId}:${arrival.stopId}`,
    feedId,
    lat: arrival.stopLat || arrival.lat,
    lon: arrival.stopLng || arrival.lon,
    stopName: arrival.stopName,
  };
};

const sortArrivalsByRoute = arrivals =>
  sortBy(arrivals, [
    arrival => arrival.route.routeSortOrder,
    'vanityName',
    'stopName',
  ]);

// TODO: The variables in this and other functions could use more descriptive names instead of 'a', for example
export const mapAndSortArrivals = (arrivals, stops, routes) => {
  // When service is temporarily suspended at a stop, there are no arrivals for it. See PUBWEB-2257
  if (!arrivals) {
    return [];
  }

  return sortArrivalsByRoute(
    arrivals
      .map(a => {
        // Check for id if locid is null due to some result sets giving us
        // different schema
        const stop = stops.find(
          s =>
            String(s.locid || s.stopId || s.id) === String(a.locid) &&
            s.feedId === a.feedId
        );

        // With arrivalsSharedStops, an arrival's locid may not match any stop
        // in the response. Skip rather than crash on stop.desc etc.
        if (!stop) return null;

        const { feet, fullSign, id, showMilesAway, signRoute, status } = a;

        return {
          feet,
          fullSign,
          id,
          showMilesAway,
          signRoute,
          status,

          estimatedTime: a.estimated,
          scheduledTime: a.scheduled,
          route: mapRoute(
            routes?.find(
              r =>
                r.id.toString() === a.route.toString() &&
                (!a.feedId || r.feedId === a.feedId)
            ) ?? {
              id: a.route,
              feedId: a.feedId,
              routeColor: a.routeColor,
              routeSubType: a.routeSubType,
            }
          ),
          vanityName: parseSignVanityName(a.fullSign, a.route),
          /* TODO: These fields currently have different names for the same info
        because FavoriteStops and NearbyStops currently have different schema.
        We should unify those, probably using the NearbyStops one */
          stopName: stop.desc || stop.stopName,
          stopId: stop.locid || stop.stopId || stop.id,
          feedId: stop.feedId || null,
          stopFeetDistance: stop.feetDistance || stop.stopFeetDistance,
          dir: stop.dir,
          stopMetersDistance: stop.metersDistance || stop.stopMetersDistance,
          stopLat: stop.lat || stop.stopLat,
          stopLng: stop.lng || stop.stopLng,
          vehicleId: a.vehicleID || null,
          blockId: a.blockID || null,
          dropOffOnly: a.dropOffOnly,
        };
      })
      .filter(Boolean)
  );
};

export const createSignKey = fullSign =>
  fullSign.toLowerCase().replace(/([^a-z0-9])/g, '_');

export const mapSignsForStop = (arrivals, routes, alerts) => {
  if (!arrivals?.length) {
    return null;
  }

  const signs = {};

  arrivals.forEach(a => {
    const key = createSignKey(a.fullSign);

    if (!signs[key]) {
      const { signRoute } = a;
      const rawRoute = routes?.find(
        r => r.id === a.route.id && r.feedId === a.route.feedId
      );
      const route = mapRoute({
        // Fall back to a.route (already a mapRoute result) when the raw
        // routes array is absent or doesn't contain this route
        ...(rawRoute ?? a.route),

        // this indicates the route number displayed on the head sign
        // which is occasionally different from the route number
        // associated with the arrival in the schedule data
        signRoute,
      });

      signs[key] = {
        route,
        arrivals: [],
        vanityName: parseSignVanityName(a.fullSign, route.id),
        alerts: alerts
          ? alerts.filter(alert => alert.route?.some(r => r.id === route.id))
          : [],
      };
    }
    signs[key].arrivals.push(a);
  });

  return signs;
};

export const mapVehicleStopsAndArrivals = (stops, arrivals) => {
  return stops.map(stop => {
    // TODO arrivals endpoint doesn't always return results for all requested locIDs, patching for now
    const arrival = arrivals.find(a => String(a.locid) === String(stop.locid));
    const minimalStop = {
      id: stop.locid.toString(),
      stopId: stop.locid,
      stopName: stop.desc,
      lat: stop.lat,
      lon: stop.lng,
    };
    if (arrival) {
      return {
        ...minimalStop,
        dir: arrival.dir,
        estimatedTime: arrival.estimated || null,
        scheduledTime: arrival.scheduled || null,
        feet: arrival.feet,
        showMilesAway: arrival.showMilesAway,
        status: arrival.status,
        dropOffOnly: arrival.dropOffOnly,
      };
    }
    return minimalStop;
  });
};

// Favorite Stops are really Arrivals
export const mapFaveStopArrivalToStop = stopArrival => ({
  locid: stopArrival.stopId,
  dir: stopArrival.dir,
  feetDistance: stopArrival.stopFeetDistance,
  metersDistance: stopArrival.stopMetersDistance,
  desc: stopArrival.stopName,
  lng: stopArrival.stopLng,
  lat: stopArrival.stopLat,
  isFavorite: true,
});

export const mapFaveStopArrivalsToStops = stops =>
  stops.map(stop => mapFaveStopArrivalToStop(stop));

/**
 * Process attributes of the input stop object to generate a return object that
 * aligns with `otp-ui`'s schema for a transit stop
 */
export const mapStopToOtp = stop => {
  const id = String(stop.id || stop.locid);

  return {
    ...stop,
    id,
    stopId: id,
    lon: stop.lng,
    name: stop.desc,
  };
};

export const getEtaOrScheduledTime = arrival => {
  if (arrival.estimatedTime != null) {
    return arrival.estimatedTime;
  }
  return arrival.scheduledTime;
};

/**
 * Filters arrivals to only those matching stops in the supplied array.
 *
 * @param {*[]} arrivals - Array of arrival objects to filter
 * @param {*[]} stops - Array of stop objects to match against
 * @param {Object} [options] - Configuration options
 * @param {boolean} [options.useRoute=false] - If true, requires both stop ID and route ID
 *   to match; if false, only stop ID needs to match
 * @returns {*[]} Filtered array of arrivals matching the provided stops
 */
export const filterArrivalsByStops = (
  arrivals,
  stops = [],
  { useRoute = false } = {}
) => {
  if (stops.length === 0) return [];

  return arrivals.filter(arrival => {
    // Normalize arrival to namespaced format (feedId:stopId)
    // Default to TRIMET feed for null/undefined feedId values
    const arrivalNamespacedId = formatNamespacedStopId(arrival);

    return stops.some(stop => {
      // Normalize stop to namespaced format (feedId:stopId)
      const stopNamespacedId = formatNamespacedStopId(stop);

      // Check if stop IDs match (includes feed comparison via namespaced ID)
      if (stopNamespacedId !== arrivalNamespacedId) return false;

      // If route matching not required, stop ID match is sufficient
      if (!useRoute) return true;

      // For favorite stops, also verify route ID matches
      return String(stop.route?.id) === String(arrival.route?.id);
    });
  });
};

/** from a stop arrival record (e.g., has stop.signs[].arrivals[]), return the arrival record for given block */
export const findArrivalByBlock = (stop, block) => {
  let retVal = null;
  try {
    Object.values(stop.signs).forEach(sign => {
      const found = sign.arrivals.find(a => a.blockId.toString() === block);
      if (found) retVal = found;
    });
  } catch {
    retVal = null;
  }
  return retVal;
};
